import { useCallback, useEffect, useRef, useState } from 'preact/hooks'
import { ArrowRight, Loader2, Star, Trash2 } from 'lucide-preact'
import { TextBox } from '@/components/common/TextBox'
import { BRAND_CLASSES } from '@/constants/theme'
import { signedOffClause } from '@/components/landingPageTemplates/templateText'
import { useLandingPageTemplateStatus } from '@/hooks/useLandingPageTemplateStatus'
import { useToast } from '@/hooks/useToast'
import { fieldLimits } from '@/types/field-limits.generated'
import { routes } from '@/utils/routes'

interface Props {
    clientCompanyId: string
    workspaceId: string
    webId: string
    /** Prefills the template name. */
    webName: string
}

type Pane = 'save' | 'saved'

const LABEL_CELL = 'text-sm font-medium text-slate-400 dark:text-slate-500 uppercase tracking-wider pr-4 align-middle w-20'
const ACTION_BUTTON = 'inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-md border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors'
const GHOST_BUTTON = 'inline-flex items-center px-3 py-1.5 text-sm rounded-lg text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors disabled:opacity-50'
const HELP_TEXT = 'text-xs leading-[18px] text-slate-500 dark:text-slate-400'
const WARNING_TEXT = `text-xs leading-[18px] ${BRAND_CLASSES.INPUT.HELPER_WARNING}`

/**
 * The TEMPLATE row of the Landing Page tab's actions (ORB-1103): the star that saves this web's landing page as a
 * workspace template, and once saved, the popover that links to the Templates page and removes it. The row appears
 * only when the server says the page may be saved, or when it already is a template so it can still be removed here.
 * A page started from a template may still be saved, but is warned of as a near-copy of it.
 */
export function SaveAsTemplateControl({ clientCompanyId, workspaceId, webId, webName }: Props) {
    const { showSuccess } = useToast()
    const { status, error, saveTemplate, removeTemplate } = useLandingPageTemplateStatus(workspaceId, webId)
    const [pane, setPane] = useState<Pane | null>(null)
    const [name, setName] = useState('')
    const [confirmingRemove, setConfirmingRemove] = useState(false)
    const [busy, setBusy] = useState(false)
    const anchorRef = useRef<HTMLSpanElement>(null)
    const triggerRef = useRef<HTMLButtonElement>(null)
    const popoverRef = useRef<HTMLDivElement>(null)
    const keepRef = useRef<HTMLButtonElement>(null)

    const close = useCallback((restoreFocus: boolean) => {
        setPane(null)
        setConfirmingRemove(false)
        if (restoreFocus) triggerRef.current?.focus()
    }, [])

    useEffect(() => {
        if (!pane) return
        const onMouseDown = (event: MouseEvent) => {
            if (!anchorRef.current?.contains(event.target as Node)) close(false)
        }
        document.addEventListener('mousedown', onMouseDown)
        return () => document.removeEventListener('mousedown', onMouseDown)
    }, [pane, close])

    useEffect(() => {
        const popover = popoverRef.current
        if (!popover) return
        if (pane === 'save') {
            const input = popover.querySelector('input')
            input?.focus()
            input?.select()
        } else if (confirmingRemove) {
            keepRef.current?.focus()
        } else {
            popover.focus({ preventScroll: true })
        }
    }, [pane, confirmingRemove])

    if (error) {
        return (
            <tr>
                <td className={LABEL_CELL}>Template</td>
                <td className="align-middle text-sm text-red-600 dark:text-red-400">{error}</td>
            </tr>
        )
    }
    if (!status || (!status.CanSaveAsTemplate && !status.Template)) return null

    const template = status.Template
    const latest = status.LatestSignedOffVersion
    const startedFromTemplateName = status.StartedFromTemplateName

    const toggle = () => {
        if (pane) {
            close(true)
            return
        }
        if (template) {
            setPane('saved')
            return
        }
        setName(webName)
        setPane('save')
    }

    const handleSave = async () => {
        const trimmed = name.trim()
        if (!trimmed || busy) return
        setBusy(true)
        const saved = await saveTemplate(trimmed)
        setBusy(false)
        if (!saved) return
        close(true)
        showSuccess('Saved to Templates', `New landing pages can start from “${trimmed}”.`)
    }

    const handleRemove = async () => {
        if (busy) return
        setBusy(true)
        const removed = await removeTemplate()
        setBusy(false)
        if (!removed) return
        close(true)
        showSuccess('Removed from Templates', 'The landing page itself isn’t changed.')
    }

    const onPopoverKeyDown = (event: KeyboardEvent) => {
        if (event.key !== 'Escape') return
        event.preventDefault()
        if (confirmingRemove) setConfirmingRemove(false)
        else close(true)
    }

    const savedHelp = !status.CanSaveAsTemplate && status.CannotSaveAsTemplateReason
        ? `New landing pages can’t start from it right now. ${status.CannotSaveAsTemplateReason}`
        : `New landing pages started from it use the latest signed-off version${latest ? `, v${latest.VersionNumber} right now` : ''}.`

    return (
        <tr>
            <td className={LABEL_CELL}>Template</td>
            <td className="align-middle">
                <div className="flex items-center gap-2 flex-wrap">
                    <span ref={anchorRef} className="relative inline-flex">
                        <button
                            ref={triggerRef}
                            type="button"
                            onClick={toggle}
                            aria-haspopup="dialog"
                            aria-expanded={pane !== null}
                            className={ACTION_BUTTON}
                        >
                            {template
                                ? <><Star aria-hidden="true" fill="currentColor" className="w-4 h-4 text-amber-500 dark:text-amber-400" /> Saved as template</>
                                : <><Star aria-hidden="true" className="w-4 h-4 text-amber-500 dark:text-amber-400" /> Save as template</>}
                        </button>

                        {/* Opens upward: this is the card's last row, and the workspace page clips whatever hangs below the page. */}
                        {pane && (
                            <div
                                ref={popoverRef}
                                role="dialog"
                                aria-label={pane === 'save' ? 'Save as template' : 'Saved as template'}
                                tabIndex={-1}
                                onKeyDown={onPopoverKeyDown}
                                className="absolute left-0 bottom-full mb-2 z-30 w-80 p-4 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 shadow-lg text-left focus:outline-none"
                            >
                                {pane === 'save' ? (
                                    <div className="flex flex-col gap-3">
                                        <p className="text-sm font-semibold text-slate-900 dark:text-white">Save as template</p>
                                        {startedFromTemplateName && (
                                            <p className={WARNING_TEXT}>
                                                This page was started from the template “{startedFromTemplateName}”. Unless its design has changed since, the new template will be a near-copy of it.
                                            </p>
                                        )}
                                        <TextBox
                                            name="save-template-name"
                                            label="Template name"
                                            density="compact"
                                            value={name}
                                            onChange={setName}
                                            maxLength={fieldLimits.LandingPageTemplate.Name.MaxLength}
                                            autoComplete="off"
                                            disabled={busy}
                                            onKeyDown={(event) => {
                                                if (event.key !== 'Enter') return
                                                event.preventDefault()
                                                void handleSave()
                                            }}
                                        />
                                        <p className={HELP_TEXT}>
                                            Uses the latest signed-off version each time.{latest ? ` Right now that’s ${signedOffClause(latest)}.` : ''}
                                        </p>
                                        <div className="flex justify-end gap-2">
                                            <button type="button" onClick={() => close(true)} disabled={busy} className={GHOST_BUTTON}>
                                                Cancel
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => void handleSave()}
                                                disabled={busy || !name.trim()}
                                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                {busy ? <><Loader2 aria-hidden="true" className="w-4 h-4 animate-spin" /> Saving…</> : 'Save template'}
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="flex flex-col gap-3">
                                        <p className="text-sm font-semibold text-slate-900 dark:text-white">Saved as template</p>
                                        <p className="-mt-1.5 text-sm font-medium text-slate-800 dark:text-slate-200 break-words">{template?.Name}</p>
                                        <p className={HELP_TEXT}>{savedHelp}</p>
                                        <a
                                            href={routes.scoped(clientCompanyId, workspaceId).templates()}
                                            className="inline-flex items-center gap-1 self-start rounded text-sm font-medium text-primary-700 dark:text-primary-400 hover:text-primary-800 dark:hover:text-primary-300 hover:underline"
                                        >
                                            View templates <ArrowRight aria-hidden="true" className="w-3.5 h-3.5" />
                                        </a>
                                        <div className="h-px bg-slate-200 dark:bg-slate-700" />
                                        {confirmingRemove ? (
                                            <div role="group" aria-label="Confirm removal" className="flex flex-col gap-1">
                                                <p className="text-sm font-medium text-slate-900 dark:text-white">Remove this template?</p>
                                                <p className={HELP_TEXT}>The landing page itself isn’t changed.</p>
                                                <div className="mt-2 flex justify-end gap-2">
                                                    <button
                                                        ref={keepRef}
                                                        type="button"
                                                        onClick={() => setConfirmingRemove(false)}
                                                        disabled={busy}
                                                        className={GHOST_BUTTON}
                                                    >
                                                        Keep
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => void handleRemove()}
                                                        disabled={busy}
                                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                                    >
                                                        {busy ? <><Loader2 aria-hidden="true" className="w-4 h-4 animate-spin" /> Removing…</> : 'Remove template'}
                                                    </button>
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="flex items-center justify-between gap-2">
                                                <button
                                                    type="button"
                                                    onClick={() => setConfirmingRemove(true)}
                                                    className="-ml-2 inline-flex items-center gap-1.5 px-2 py-1.5 text-sm font-medium rounded-lg text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-700 transition-colors"
                                                >
                                                    <Trash2 aria-hidden="true" className="w-3.5 h-3.5" /> Remove from templates
                                                </button>
                                                <button type="button" onClick={() => close(true)} className={GHOST_BUTTON}>
                                                    Close
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        )}
                    </span>
                </div>
            </td>
        </tr>
    )
}
